import {
  IconAtom,
  IconBell,
  IconBellGray,
  IconBinocularsGray,
  IconChainLink,
  IconChainLinkGray,
  IconChartBar,
  IconChartBarGray,
  IconChartRadius,
  IconClockGray,
  IconCloudCheckGray,
  IconDashboard,
  IconLocation,
  IconMeter,
  IconPcLine,
  IconPlug,
  IconRoad,
  IconRoom,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconSecurity,
  IconShareGray,
  IconTagGray,
  IconTools,
  IconTvocGray,
  IconUserCheck,
  IconWarning,
  IconWarningGray,
  IconZoneGray,
} from '@assets/images/icon';
import { IconTemp } from '@assets/images/main';
import { rtlsSystem } from '@assets/images/solution';

import integrationVideo from '@src/assets/images/main/video/integration.mp4';

import image2 from '@src/assets/images/solution/medicalWaste/img/2.avif';
import image3 from '@src/assets/images/solution/medicalWaste/img/3.avif';
import image4 from '@src/assets/images/solution/medicalWaste/img/4.avif';
import image5 from '@src/assets/images/solution/medicalWaste/img/5.avif';
import image6 from '@src/assets/images/solution/medicalWaste/img/6.avif';
import image7 from '@src/assets/images/solution/medicalWaste/img/7.avif';
import image8 from '@src/assets/images/solution/medicalWaste/img/8.avif';
import image9 from '@src/assets/images/solution/medicalWaste/img/9.avif';

export default {
  type: 'Medical Solution',

  banner: {
    title: 'Optimize medical waste management with a robot-based automated system.',
    desc: 'Combine robots with the ORBRO location tracking system to monitor the location of medical waste in real time and provide automated operational management.',
  },

  video: {
    title: 'Experience smart medical waste management with integrated robots and tracking systems.',
    desc: 'By integrating robot-based medical waste management with ORBRO’s location tracking system, you can achieve accurate location monitoring and automated management, greatly improving operational efficiency.',
    play: integrationVideo,
  },

  func: [
    {
      theme: 'Monitor robot positions in real time and manage medical waste efficiently.',
      title: 'Real-time robot tracking and operation monitoring',
      desc: 'Accurately track robot positions and operational status with the ORBRO tracking system, enhancing the precision of medical waste management and maximizing operational efficiency through immediate response.',
      sub1: 'High accuracy',
      icon1: IconRulerGray,
      sub2: 'Fast data integration',
      icon2: IconShareGray,
      image: image2,
    },
    {
      title: 'Autonomous travel to the loading area',
      desc: 'Robots safely navigate autonomously to the loading area, performing tasks without human intervention, increasing efficiency and ensuring worker safety.',
      sub1: 'Risk asset assignment',
      icon1: IconWarningGray,
      sub2: 'Response configuration',
      icon2: IconBellGray,
      image: image3,
    },
    {
      title: 'Automated linkage with loading equipment',
      desc: 'Build an automated linkage system between loading equipment and robots, smoothly managing robot calls and loading readiness to significantly boost work efficiency and processing speed.',
      sub1: 'High accuracy',
      icon1: IconRulerGray,
      sub2: 'Fast data integration',
      icon2: IconChainLinkGray,
      image: image4,
    },
    {
      title: 'Smart locker integration',
      desc: 'Effectively integrate with smart lockers to enable easy data access and asset management, improving operational convenience and accuracy.',
      sub1: 'Statistical data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image5,
    },
    {
      theme: 'Monitor equipment status in real time and automate disinfection management.',
      title: 'Real-time work status monitoring',
      desc: 'Monitor each robot’s work status in real time, allowing immediate action when issues arise, minimizing errors during operations, and ensuring a safe management environment.',
      sub1: 'Statistical data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image6,
    },
    {
      title: 'Manage multiple robots simultaneously',
      desc: 'Manage multiple robots within a single system simultaneously to improve management efficiency, prevent overlapping tasks and collisions, and support accurate and smooth operations.',
      sub1: 'Statistical data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image7,
    },
    {
      title: 'Integration and management with disinfection systems',
      desc: 'Easily integrate with plasma, UVC, and HPV disinfection devices to perform fast and precise sterilization of medical waste and tools, maintaining a hygienic environment.',
      sub1: 'Statistical data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image8,
    },
    {
      title: 'Real-time equipment status monitoring',
      desc: 'Monitor the connection and operation status of each disinfection device in real time to identify potential issues during operations and take immediate action.',
      sub1: 'Statistical data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image9,
    },
  ],

  subfunc: {
    title: 'Key Features Overview',
    desc: '',

    data: [
      {
        icon: IconLocation,
        title: 'Real-time robot tracking',
        desc: 'Accurately track the current position of robots and monitor operational status in real time.',
      },
      {
        icon: IconMeter,
        title: 'Real-time work status monitoring',
        desc: 'Detect robot work progress in real time for immediate response.',
      },
      {
        icon: IconUserCheck,
        title: 'Simultaneous multi-robot management',
        desc: 'Efficiently manage multiple robots at once to enhance operational accuracy.',
      },
      {
        icon: IconRoad,
        title: 'Autonomous robot travel to loading area',
        desc: 'Robots autonomously travel to the loading area, improving efficiency and safety.',
      },
      {
        icon: IconPlug,
        title: 'Automated linkage with loading equipment',
        desc: 'Automatically manage robot calls and loading preparation to increase operational efficiency.',
      },
      {
        icon: IconPcLine,
        title: 'Dedicated robot management dashboard',
        desc: 'Provides an intuitive dashboard to manage all robot operations and data.',
      },
      {
        icon: IconRoom,
        title: 'Smart locker system integration',
        desc: 'Integrate with lockers to efficiently manage medical waste storage status.',
      },
      {
        icon: IconTools,
        title: 'Real-time disinfection device monitoring',
        desc: 'Monitor each disinfection device’s connection and operation status in real time, ensuring stable operation.',
      },
      {
        icon: IconAtom,
        title: 'Integration with various disinfection systems',
        desc: 'Easily link with plasma, UVC, and HPV disinfection devices for fast and thorough sterilization.',
      },
      {
        icon: IconChartBar,
        title: 'Real-time integrated operation management',
        desc: 'Manage the operational status and inventory of all equipment on a single screen, supporting quick decision-making.',
      },
    ],
  },

  system: {
    title: 'System Configuration',
    img: rtlsSystem,
  },

  productTitle: 'Product Packages',
  product: [
    {
      tag: 'assetPackage',
    },
    {
      tag: 'workerPackage',
    },
  ],

  contents: [
    {
      data: 'medicalPatient',
    },
    {
      data: 'officeAsset',
    },
    {
      data: 'blogMedical',
    },
    {
      data: 'blogRTLS',
    },
  ],
};
